import moment from 'moment';
import Tooltip from '@mui/material/Tooltip';
import { dateMomentFormat } from 'constants';
import Typography from '@mui/material/Typography';
import { parse, isBefore, isEqual, startOfDay } from 'date-fns';
import { ActionWrapperBox } from 'components/Common/common.styled';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import { ButtonIcon, RoundedIconVisibility } from 'components/Ui/styled';
import { renderCellExpand } from 'components/Common/GridCell/GridCellExpand';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';

const renderBookedSessionsV2Actions = (
  params,
  handleStatus,
  handleCancel,
  handleViewBookedInfo,
) => {
  const sessionDate = startOfDay(
    parse(params.row.date, 'MM-dd-yyyy', new Date()),
  );

  const today = startOfDay(new Date());

  const isTodayOrPast =
    isEqual(sessionDate, today) || isBefore(sessionDate, today);

  const isFuture = isBefore(today, sessionDate);

  const isCancelled = params.row.status === 'cancelled';

  return (
    <ActionWrapperBox>
      {isTodayOrPast && (
        <ButtonIcon disabled={isCancelled} onClick={() => handleStatus(params)}>
          <Tooltip title="Status" arrow={false} placement="top">
            <BarChartRoundedIcon />
          </Tooltip>
        </ButtonIcon>
      )}
      {isFuture && (
        <ButtonIcon disabled={isCancelled} onClick={() => handleCancel(params)}>
          <Tooltip title="Cancel" arrow={false} placement="top">
            <HighlightOffRoundedIcon />
          </Tooltip>
        </ButtonIcon>
      )}
      <ButtonIcon onClick={() => handleViewBookedInfo(params)}>
        <RoundedIconVisibility />
      </ButtonIcon>
    </ActionWrapperBox>
  );
};

export const bookedSessionV2Columns = (
  handleStatus,
  handleCancel,
  handleViewBookedInfo,
) => [
  {
    field: 'action',
    headerName: 'Action',
    sortable: false,
    disableColumnMenu: true,
    width: 80,
    renderCell: (params) =>
      renderBookedSessionsV2Actions(
        params,
        handleStatus,
        handleCancel,
        handleViewBookedInfo,
      ),
  },
  {
    headerName: 'Name',
    field: 'studentName',
    width: 200,
    renderCell: (params) => {
      const studentFirstName = params.row.studentFirstName;
      const studentLastName = params.row.studentLastName;
      return (
        <Typography variant="base">
          {studentFirstName} {studentLastName}
        </Typography>
      );
    },
  },
  { headerName: 'Subject', field: 'subjectName', width: 150 },
  {
    headerName: 'Date',
    field: 'date',
    type: 'date',
    width: 150,
  },
  { headerName: 'Day', field: 'dayOfWeek', width: 150 },
  {
    headerName: 'Time',
    field: 'time',
    width: 160,
    renderCell: (params) => {
      const startTimeFmt = params.row.startTimeFmt;
      const endTimeFmt = params.row.endTimeFmt;
      return (
        <Typography variant="base">
          {startTimeFmt} {endTimeFmt}
        </Typography>
      );
    },
  },
  { headerName: 'Status', field: 'status', width: 150 },
  { headerName: 'Grade', field: 'studentGradeLevel', width: 150 },
  {
    headerName: 'Note',
    field: 'statusNote',
    renderCell: renderCellExpand,
    width: 150,
  },
  {
    headerName: 'Reason',
    field: 'reason',
    renderCell: renderCellExpand,
    width: 150,
  },
];

export const getBookedSessionV2ListData = (bookedSessionListData) => {
  const items = bookedSessionListData?.items;
  if (!items || items.length === 0) {
    return [];
  }
  return items.map(
    ({
      sessionId,
      date,
      timeSlotId,
      dayOfWeek,
      startTime,
      startTimeFmt,
      endTime,
      endTimeFmt,
      subjectId,
      subjectName,
      studentId,
      studentFirstName,
      studentLastName,
      studentGradeLevel,
      reason,
      status,
      statusNote,
    }) => ({
      id: sessionId,
      sessionId,
      date: moment(date).format(dateMomentFormat),
      timeSlotId,
      dayOfWeek,
      startTime,
      startTimeFmt,
      endTime,
      endTimeFmt,
      subjectId,
      subjectName,
      studentId,
      studentFirstName,
      studentLastName,
      studentGradeLevel,
      reason,
      status: status ? status.charAt(0).toUpperCase() + status.slice(1) : '',
      statusNote,
    }),
  );
};
